import {
  TableRow,
  alpha,
  TableCell,
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
} from '@mui/material';
import { memo } from 'react';
import { generateAttributeLabel } from '../assembliesAndItems/items/AddedItemRow';
import { ExpandedRowProps, ItemsTableProps } from './types';
import { Calculations as calc } from '../../utils/calculations';

// Memoized expanded row component with better memo comparison
export const ExpandedRow = memo(({ assembly, theme, currentBreakouts }: ExpandedRowProps) => {
  return (
    <TableRow
      sx={{
        backgroundColor: `${
          theme.palette.mode === 'dark'
            ? theme.palette.background.paper
            : alpha(theme.palette.common.black, 0.02)
        } !important`, // Force the background color with !important
        width: '100%',
      }}
    >
      <TableCell
        colSpan={6}
        sx={{
          p: 0,
          width: '100%',
          backgroundColor: 'transparent !important', // Ensure cell background doesn't override row background
          borderBottom: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.2 : 0.1)}`,
        }}
      >
        <Box
          sx={{
            pl: 2,
            pr: 1,
            py: 1,
            width: '100%',
            borderLeft: `3px solid ${alpha(theme.palette.primary.main, 0.5)}`,
            ml: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1,
              fontSize: '0.8rem',
              color: theme.palette.primary.main,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            Breakout: {assembly.itemsList?.[0]?.breakout?.breakoutname || 'None'}
          </Typography>

          <ItemsTable
            key={`items-${assembly.assemblyid}-${assembly.lastupdated || 'fresh'}`}
            assembly={assembly}
            theme={theme}
            currentBreakouts={currentBreakouts}
          />
        </Box>
      </TableCell>
    </TableRow>
  );
});

// ItemsTable component with better memo comparison
const ItemsTable = memo(({ assembly, theme, currentBreakouts }: ItemsTableProps) => {
  // Pre-compute the expensive operation once
  const itemsWithQty = assembly.itemsList.map(item => {
    // Calculate item quantity using the most up-to-date assembly data
    const qty = calc.getItemQtyByTakeoffVariable(assembly, item);
    return { item, qty };
  });

  // Direct theme color references to ensure updates
  const primaryColor = theme.palette.text.primary;

  return (
    <Table
      key={`table-${assembly.assemblyid}-${assembly.lastupdated || 'fresh'}`}
      size="small"
      sx={{
        backgroundColor: 'transparent',
        width: '100%',
        tableLayout: 'fixed',
        // Reset any inherited styles that might affect children
        '& .MuiTableRow-root': {
          backgroundColor: 'transparent',
        },
        // Ensure all text has the proper color
        '& *': {
          color: `${primaryColor} !important`,
        },
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell
            sx={{
              fontWeight: 600,
              backgroundColor: `${
                theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5'
              } !important`, // Force consistent styling
              color: `${primaryColor} !important`,
              borderBottom: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.2 : 0.1)}`,
              fontSize: '0.75rem',
              p: 1,
              width: '80%',
            }}
          >
            Item Name
          </TableCell>
          <TableCell
            sx={{
              fontWeight: 600,
              backgroundColor: `${
                theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5'
              } !important`, // Force consistent styling
              color: `${primaryColor} !important`,
              borderBottom: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.2 : 0.1)}`,
              fontSize: '0.75rem',
              p: 1,
              width: '20%',
            }}
          >
            QTY
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {itemsWithQty.map(({ item, qty }: { item: any; qty: number }, idx: number) => {
          // Define the background color explicitly based on index and theme
          const bgColor =
            theme.palette.mode === 'dark'
              ? idx % 2 === 0
                ? theme.palette.background.paper // Even rows
                : alpha(theme.palette.common.white, 0.02) // Odd rows (slightly lighter)
              : idx % 2 === 0
                ? theme.palette.background.default // Even rows in light mode
                : alpha(theme.palette.common.black, 0.02); // Odd rows in light mode

          return (
            <TableRow
              key={`${assembly.assemblyid}-${item.itemid}-${idx}`}
              sx={{
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                backgroundColor: `${bgColor} !important`, // Force consistent color with !important
                '&:hover': {
                  backgroundColor: `${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.05)} !important`,
                },
                // Force text colors
                '& *': {
                  color: `${primaryColor} !important`,
                },
                width: '100%',
              }}
            >
              <TableCell
                sx={{
                  fontSize: '0.75rem',
                  p: 1,
                  color: `${primaryColor} !important`,
                  backgroundColor: 'transparent !important', // Ensure the cell doesn't override row
                  borderBottom:
                    idx === assembly.itemsList.length - 1
                      ? 'none'
                      : `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.2 : 0.1)}`,
                  width: '80%',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {generateAttributeLabel(
                  {
                    attributevaluename: item.attribute?.attributevaluename || 'Unnamed',
                  },
                  item
                )}
              </TableCell>
              <TableCell
                sx={{
                  fontSize: '0.75rem',
                  p: 1,
                  fontWeight: 500,
                  color:
                    theme.palette.mode === 'dark'
                      ? `${theme.palette.primary.light} !important`
                      : `${theme.palette.primary.main} !important`,
                  backgroundColor: 'transparent !important', // Ensure the cell doesn't override row
                  borderBottom:
                    idx === assembly.itemsList.length - 1
                      ? 'none'
                      : `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.2 : 0.1)}`,
                  width: '20%',
                  textAlign: 'right',
                }}
              >
                {qty}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
});
